import { hasPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import { BankerSearch } from 'pages/Admin/AdminTools/BankerSearch'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ButtonGroup } from 'stories/components'

import { ManageAccountTypes } from './AccountTypes'
import { BorrowerPipelineAttentionTitles } from './BorrowerPipelineAttentionTitles'
import { Bubbles } from './Bubbles'
import { BudgetTemplates } from './BudgetTemplates'
import { CollateralReviewConfiguration } from './CollateralReview'
import { ConfigSearch } from './ConfigSearch'
import { CounterPartyConfig } from './CounterParty'
import { CreditReportDefaultsConfigPage } from './CreditReportDefaults'
import { Documents } from './Documents'
import { EmailSetup } from './EmailSetup'
import { ExceptionRequestConfig } from './ExceptionRequest'
import { Features } from './Features'
import { FeeConfiguration } from './Fee'
import { FieldProperties } from './FieldProperties'
import { FloaterTabsConfig } from './FloaterTabs'
import { FooterConfiguration } from './Footer'
import { FormDefaultsConfig } from './FormDefaults'
import { GlobalSettings } from './GlobalSettings'
import { HMDAConfiguration } from './HMDA'
import { HubspotSyncConfig } from './HubspotSync'
import { HUD1Configuration } from './HUD1'
import { Interface } from './Interface'
import { Links } from './Links'
import { LoanOverviewAccess } from './LoanOverviewAccess'
import { LoanPipelineHeadersByAccountTypes } from './LoanPipelineHeadersByAccountTypes'
import { LoanStatusLabelPage } from './LoanStatusLabel'
import { LoanStatusRestrictions } from './LoanStatusRestrictions'
import { LoanActions } from './LoanSubmission'
import { LoanSubmissionHierarchy } from './LoanSubmissionHierarchy'
import { menus } from './menus'
import { MLDSConfiguration } from './MLDS'
import { MortgageCallReportConfiguration } from './MortgageCallReport'
import { OrderValuation } from './OrderValuation'
import { PaymentTermsConditionsConfig } from './PaymentTermsConditionsConfig'
import { PrintMailConfig } from './PrintMail'
import { revealConfigTarget } from './revealTarget'
import { RulesConfiguration } from './RulesConfiguration'
import { ConfigSearchEntry } from './searchIndex'
import { SecondaryMarketing } from './SecondaryMarketing'
import { ServicingConfiguration } from './Servicing'
import { ServicingInformationConfig } from './ServicingInformation'
import { StaticFiles } from './StaticFiles'
import { SwitchUserVisibility } from './SwitchUserVisibility'
import { TasksConfig } from './Tasks'
import { TextConfiguration } from './TextsConfiguration'
import { UnderwritingConfig } from './Underwriting'
import { Workflows } from './Workflows/Workflows'

// `menus` lives in ./menus so the search index can use it without importing this file.
export { menus }

export const Configuration = (props: { setChanged?: Function }) => {
  const { hasPermission } = usePermissions()
  const location = useLocation()
  const navigate = useHistory()
  const query = new URLSearchParams(location.search)
  const sectionKey = query.get('section') || ''
  const sectionTab = query.get('tab') || ''
  const initialMenu =
    sectionKey && sectionKey in menus ? menus[sectionKey as keyof typeof menus] : menus.globalSettings
  const [selectedMenu, setSelectedMenu] = useState(initialMenu)
  const [changed, _setChanged] = useState(false)
  const [jumpTarget, setJumpTarget] = useState<{ entry: ConfigSearchEntry; nonce: number } | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    props.setChanged && props.setChanged(changed)
  }, [changed])

  useEffect(() => {
    const section = new URLSearchParams(location.search).get('section')
    if (section && section in menus) setSelectedMenu(menus[section as keyof typeof menus])
  }, [location.search])

  const onChangeMenu = async (value: any) => {
    if (changed) {
      const isContinue = await confirm(`You've made some changes!\nAre you sure want to leave without Saving?`)
      if (!isContinue) return
    }
    setSelectedMenu(value)
    _setChanged(false)
    if (location.search) navigate.replace('/admin_tool/configuration')
  }

  // A search result opens its section, then scrolls to and flashes the setting.
  const onJump = async (entry: ConfigSearchEntry) => {
    if (entry.menu !== selectedMenu) {
      if (changed) {
        const isContinue = await confirm(`You've made some changes!\nAre you sure want to leave without Saving?`)
        if (!isContinue) return
      }
      setSelectedMenu(entry.menu)
      _setChanged(false)
    }
    setJumpTarget({ entry, nonce: Date.now() })
  }

  useEffect(() => {
    if (!jumpTarget) return
    return revealConfigTarget(() => contentRef.current, jumpTarget.entry)
  }, [jumpTarget])

  const renderContent = useMemo(() => {
    switch (selectedMenu) {
      case menus.globalSettings:
        return <GlobalSettings />
      case menus.fieldProperties:
        return <FieldProperties setChanged={_setChanged} />
      case menus.loanAction:
        return <LoanActions setChanged={_setChanged} />
      case menus.loanStatusRestrictions:
        return (
          <>
            <LoanStatusRestrictions />
            <Workflows />
          </>
        )
      case menus.texts:
        return <TextConfiguration />
      case menus.emailSetup:
        return <EmailSetup initialTab={sectionKey === 'emailSetup' ? sectionTab : undefined} />
      case menus.rules:
        return <RulesConfiguration />
      case menus.servicing:
        return <ServicingConfiguration />
      case menus.links:
        return <Links />
      case menus.features:
        return <Features />
      case menus.bubbles:
        return <Bubbles />
      case menus.exceptionRequests:
        return <ExceptionRequestConfig />
      case menus.loanStatusLabel:
        return <LoanStatusLabelPage />
      case menus.interface:
        return <Interface />
      case menus.paymentTerms:
        return <PaymentTermsConditionsConfig />
      case menus.hud1:
        return <HUD1Configuration />
      case menus.footer:
        return <FooterConfiguration />
      case menus.underwriting:
        return <UnderwritingConfig initialTab={sectionKey === 'underwriting' ? sectionTab : undefined} />
      case menus.servicingInformation:
        return <ServicingInformationConfig />
      case menus.formDefaults:
        return <FormDefaultsConfig />
      case menus.fee:
        return <FeeConfiguration />
      case menus.hmda:
        return <HMDAConfiguration />
      case menus.mlds:
        return <MLDSConfiguration setChanged={_setChanged} />
      case menus.mortgageCallReport:
        return <MortgageCallReportConfiguration />
      case menus.loanSubmissionHierarchy:
        return <LoanSubmissionHierarchy />
      case menus.userSwitchVisibility:
        return <SwitchUserVisibility />
      case menus.orderValuation:
        return <OrderValuation />
      case menus.accountTypes:
        return <ManageAccountTypes />
      case menus.floaterTabs:
        return <FloaterTabsConfig />
      case menus.secondaryMarketing:
        return <SecondaryMarketing />
      case menus.counterParty:
        return <CounterPartyConfig />
      case menus.partyEligibility:
        return <BankerSearch />
      case menus.creditReportDefaults:
        return <CreditReportDefaultsConfigPage />
      case menus.loanPipelineHeaders:
        return <LoanPipelineHeadersByAccountTypes />
      case menus.tasks:
        return <TasksConfig />
      case menus.staticFiles:
        return <StaticFiles />
      case menus.documents:
        return <Documents />
      case menus.collateralReview:
        return <CollateralReviewConfiguration />
      case menus.borrowerPipelineAttentionTitles:
        return <BorrowerPipelineAttentionTitles />
      case menus.loanOverviewAccess:
        return <LoanOverviewAccess />
      case menus.budgetTemplates:
        return <BudgetTemplates />
      case menus.hubspotSync:
        return <HubspotSyncConfig />
      case menus.printMail:
        return <PrintMailConfig />
      default:
        return <></>
    }
  }, [selectedMenu, sectionKey, sectionTab])

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 pb-5">
        <h1 className="text-2xl font-bold flex items-center">Configuration</h1>
        <ConfigSearch onJump={onJump} />
      </div>

      <ButtonGroup
        title={Object.values(menus)
          .filter(
            (v) =>
              (v != 'Quick Links' || hasPermission('AdminTools.Configuration.QuickLinks')) &&
              // Print & Mail sets the rate card and the provider account the postage is billed to,
              // so it is held apart from general Admin Tools by its own permission.
              (v != menus.printMail || hasPermission('PrintMail.Configure')) &&
              (v != menus.partyEligibility ||
                (hasPermission('AdminTools.AppraiserSettlementContractor') &&
                  hasPagePermissions(['admin_tool', 'bankerSearch']))),
          )
          .sort((a, b) => a.localeCompare(b))}
        value={selectedMenu}
        onChange={(value) => onChangeMenu(value)}
      />

      <div className="p-2" ref={contentRef}>
        {renderContent}
      </div>
    </div>
  )
}
